import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { RouteWithoutChildren } from '@/shared/lib/ts-router';
import { type BreadcrumbItem, Breadcrumbs as SharedBreadcrumbs, useMainLayout } from '@/shared/ui/MainLayoutProvider';

type Props = {
  config: RouteWithoutChildren;
  className?: string;
};

export const Breadcrumbs: FC<Props> = ({ config, className }) => {
  const { i18n, t } = useTranslation();

  const {
    breadcrumbs: { setBreadcrumbs },
    isMobile,
  } = useMainLayout();

  const { pathname } = useLocation();

  const getTitle = useCallback(
    (label: RouteWithoutChildren['label']) => (label instanceof Function ? label({ i18n }) : label),
    [i18n],
  );

  const formatBreadcrumb = useCallback(
    (cfg: RouteWithoutChildren): BreadcrumbItem => {
      const label = getTitle(cfg.label);

      const path = cfg.path
        ?.split('/')
        .filter((item) => !item.startsWith(':'))
        .join('/');

      return {
        isCurrent: cfg.path === config.path,
        label,
        path,
      };
    },
    [config.path, getTitle],
  );

  const parserBreadcrumns = useCallback(
    (cfg: RouteWithoutChildren, prev?: BreadcrumbItem[]): BreadcrumbItem[] => {
      const result: BreadcrumbItem[] = [formatBreadcrumb(cfg), ...(prev || [])];

      if (cfg.parent) {
        result.splice(0, 0, ...parserBreadcrumns(cfg.parent, prev));
      }

      return result;
    },
    [formatBreadcrumb],
  );

  useEffect(() => {
    let nextBreadcrumbs = parserBreadcrumns(config);
    if (isMobile) {
      nextBreadcrumbs = nextBreadcrumbs.slice(nextBreadcrumbs.length - 1);
    }
    setBreadcrumbs(nextBreadcrumbs);
  }, [pathname, t, config, parserBreadcrumns, isMobile]);

  return <SharedBreadcrumbs className={className} />;
};
